/**
 * Pure CFO-cockpit math: cash floor, reasonable comp, and debt schedule.
 * No Firebase.
 */
import type {
  DebtInstrument,
  OwnerDistribution,
  PayrollPosition,
} from '../types/finances';
import type { CashFlowProjection } from './cashFlowProjection';
import type { TaxPlanBreakdown } from './taxPlan';

function roundMoney(n: number): number {
  return Math.round(n);
}

export interface CashFloorAnalysis {
  minimumCash: number;
  lowestBalance: number | null;
  lowestMonthKey: string | null;
  headroom: number | null;
  belowFloor: boolean;
  firstBreachMonthKey: string | null;
  /** 0 = the first forecast month (usually this month). Null if none. */
  monthsUntilBreach: number | null;
  monthsBelow: number;
}

export function analyzeCashFloor(
  projection: CashFlowProjection | null,
  minimumCash: number,
): CashFloorAnalysis {
  const floor = Math.max(0, roundMoney(minimumCash));
  if (!projection || projection.months.length === 0) {
    return {
      minimumCash: floor,
      lowestBalance: null,
      lowestMonthKey: null,
      headroom: null,
      belowFloor: false,
      firstBreachMonthKey: null,
      monthsUntilBreach: null,
      monthsBelow: 0,
    };
  }
  const lowest = projection.lowestBalance;
  const monthsBelow = projection.months.filter((m) => m.endingBalance < floor).length;
  const firstIdx = projection.months.findIndex((m) => m.endingBalance < floor);
  const first = firstIdx >= 0 ? projection.months[firstIdx] : undefined;
  return {
    minimumCash: floor,
    lowestBalance: lowest,
    lowestMonthKey: projection.lowestBalanceMonthKey,
    headroom: lowest == null ? null : lowest - floor,
    belowFloor: lowest != null && lowest < floor,
    firstBreachMonthKey: first?.monthKey ?? null,
    monthsUntilBreach: firstIdx >= 0 ? firstIdx : null,
    monthsBelow,
  };
}

export function formatMonthKeyLabel(monthKey: string | null | undefined): string | null {
  if (!monthKey) return null;
  const [y, m] = monthKey.split('-').map(Number);
  if (!y || !m) return monthKey;
  return new Date(y, m - 1, 1).toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
}

/** Tightest-month gap vs the floor — not today's cash minus the floor. */
export function cashFloorHeadroomLabel(analysis: CashFloorAnalysis): string {
  if (analysis.headroom == null) return 'Low vs floor';
  return analysis.belowFloor ? 'Short of floor' : 'Cushion at low month';
}

/** Overview / KPI line: when the floor is first breached, not how many months are under. */
export function cashFloorBreachCopy(analysis: CashFloorAnalysis): string | null {
  if (!analysis.belowFloor || analysis.monthsUntilBreach == null) return null;
  const when = formatMonthKeyLabel(analysis.firstBreachMonthKey);
  if (analysis.monthsUntilBreach === 0) return when ? `Below floor this month (${when})` : 'Below floor this month';
  if (analysis.monthsUntilBreach === 1) return when ? `Below floor next month (${when})` : 'Below floor next month';
  return when
    ? `Below floor ${when}`
    : `Below floor in ${analysis.monthsUntilBreach} mo`;
}

export interface ReasonableCompSnapshot {
  ownerW2: number;
  allW2: number;
  k1Remainder: number;
  totalOwnerComp: number;
  w2ShareOfOwner: number | null;
  flag: 'ok' | 'low_w2' | 'no_k1' | 'no_w2';
  ownerTitles: string[];
}

export function isOwnerLikePosition(position: PayrollPosition): boolean {
  if (!position.isActive) return false;
  if (position.department === 'leadership') return true;
  const t = (position.title || '').toLowerCase();
  return /\b(owner|principal|president|ceo|founder)\b/.test(t);
}

/** Saved IDs when the user has picked owners; otherwise the leadership-title guess. */
export function resolveOwnerPositionIds(
  positions: PayrollPosition[],
  saved: string[] | null | undefined,
): string[] {
  const activeIds = new Set(positions.filter((p) => p.isActive).map((p) => p.id));
  if (saved != null) return saved.filter((id) => activeIds.has(id));
  return positions.filter(isOwnerLikePosition).map((p) => p.id);
}

export function analyzeReasonableComp(
  positions: PayrollPosition[],
  tax: TaxPlanBreakdown | null,
  ownerPositionIds?: string[] | null,
): ReasonableCompSnapshot {
  const ownerIds = new Set(resolveOwnerPositionIds(positions, ownerPositionIds));
  const owners = positions.filter((p) => p.isActive && ownerIds.has(p.id));
  const ownerW2 = roundMoney(owners.reduce((s, p) => s + Math.max(0, p.annualSalary || 0), 0));
  const allW2 = tax?.annualW2 ?? roundMoney(
    positions.filter((p) => p.isActive).reduce((s, p) => s + Math.max(0, p.annualSalary || 0), 0),
  );
  const k1 = tax?.k1Remainder ?? 0;
  const totalOwnerComp = ownerW2 + k1;
  let flag: ReasonableCompSnapshot['flag'] = 'ok';
  if (ownerW2 <= 0 && k1 > 0) flag = 'no_w2';
  else if (k1 <= 0 && ownerW2 > 0) flag = 'no_k1';
  else if (totalOwnerComp > 0 && ownerW2 / totalOwnerComp < 0.25 && k1 > 0) flag = 'low_w2';
  return {
    ownerW2,
    allW2,
    k1Remainder: k1,
    totalOwnerComp,
    w2ShareOfOwner: totalOwnerComp > 0 ? ownerW2 / totalOwnerComp : null,
    flag,
    ownerTitles: owners.map((p) => p.title).filter(Boolean),
  };
}

export function distributionsYtd(draws: OwnerDistribution[], year: number): {
  taxCover: number;
  profit: number;
  other: number;
  total: number;
} {
  const ytd = draws.filter((d) => d.date.getFullYear() === year);
  const taxCover = roundMoney(ytd.filter((d) => d.kind === 'tax_cover').reduce((s, d) => s + d.amount, 0));
  const profit = roundMoney(ytd.filter((d) => d.kind === 'profit').reduce((s, d) => s + d.amount, 0));
  const other = roundMoney(ytd.filter((d) => d.kind === 'other').reduce((s, d) => s + d.amount, 0));
  return { taxCover, profit, other, total: taxCover + profit + other };
}

export interface DebtPaymentSlice {
  instrumentId: string;
  name: string;
  dueDate: Date;
  amount: number;
  kind: 'payment' | 'balloon';
}

export function expandDebtSchedule(
  instruments: DebtInstrument[],
  monthsAhead: number,
  now: Date = new Date(),
): DebtPaymentSlice[] {
  const out: DebtPaymentSlice[] = [];
  const start = new Date(now.getFullYear(), now.getMonth(), 1);
  for (const inst of instruments) {
    if (!inst.isActive) continue;
    const payment = roundMoney(inst.paymentAmount);
    const remaining = inst.remainingPayments == null ? monthsAhead : Math.max(0, inst.remainingPayments);
    const count = Math.min(monthsAhead, remaining);
    for (let i = 0; i < count; i++) {
      const month = new Date(start.getFullYear(), start.getMonth() + i, 1);
      const day = Math.min(28, Math.max(1, inst.paymentDay || 1));
      const due = new Date(month.getFullYear(), month.getMonth(), day);
      if (due < now && i === 0 && due.getMonth() === now.getMonth()) {
        // still this month — keep
      } else if (due < now) {
        continue;
      }
      if (payment > 0) {
        out.push({
          instrumentId: inst.id,
          name: inst.name || inst.lender || 'Loan',
          dueDate: due,
          amount: payment,
          kind: 'payment',
        });
      }
    }
    if (inst.balloonAmount > 0 && inst.balloonDate) {
      const balloon = inst.balloonDate;
      const windowEnd = new Date(start.getFullYear(), start.getMonth() + monthsAhead, 0);
      if (balloon >= start && balloon <= windowEnd) {
        out.push({
          instrumentId: inst.id,
          name: `${inst.name || 'Loan'} balloon`,
          dueDate: balloon,
          amount: roundMoney(inst.balloonAmount),
          kind: 'balloon',
        });
      }
    }
  }
  return out.sort((a, b) => a.dueDate.getTime() - b.dueDate.getTime());
}

export function formatPct(n: number | null): string {
  if (n == null || !Number.isFinite(n)) return '—';
  return `${Math.round(n * 100)}%`;
}
